import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { io } from "socket.io-client";
import BASE_URL from "../../baseUrl";
import {
  getAuthHeaders,
  getListingSessionKey,
  getOrCreateVisitorAnonId,
  getUserInfo,
} from "./chatUtils";
import "./chat.css";

const ListingChatModal = ({
  isOpen,
  onClose,
  listingId,
  agentId,
  listingTitle,
  initialMessage = "",
}) => {
  const [sessionId, setSessionId] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [typing, setTyping] = useState(false);
  const [status, setStatus] = useState("connecting");
  const socketRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const bottomRef = useRef(null);

  const visitorAnonId = useMemo(() => getOrCreateVisitorAnonId(), []);
  const user = useMemo(() => getUserInfo(), []);

  useEffect(() => {
    if (!isOpen) return;
    setInput(initialMessage || "");
  }, [isOpen, initialMessage]);

  useEffect(() => {
    if (!isOpen || !listingId || !agentId) return;

    const setup = async () => {
      setLoading(true);
      try {
        const key = getListingSessionKey(listingId, agentId);
        const storedSession = localStorage.getItem(key);
        let resolvedSessionId = storedSession;

        if (!resolvedSessionId) {
          const initRes = await axios.post(
            `${BASE_URL}/api/rechat/session/init`,
            { listingId, agentId, visitorAnonId },
            { headers: { ...getAuthHeaders() } }
          );
          resolvedSessionId = initRes?.data?.session?._id;
          if (resolvedSessionId) localStorage.setItem(key, resolvedSessionId);
        }

        if (!resolvedSessionId) return;
        setSessionId(resolvedSessionId);

        const msgRes = await axios.get(
          `${BASE_URL}/api/rechat/session/${resolvedSessionId}/messages`,
          {
            params: { visitorAnonId, page: 1, limit: 200 },
            headers: { ...getAuthHeaders() },
          }
        );
        setMessages(msgRes?.data?.messages || []);

        const socket = io(BASE_URL, { transports: ["websocket"] });
        socketRef.current = socket;
        socket.on("connect", () => setStatus("connected"));
        socket.on("disconnect", () => setStatus("disconnected"));
        socket.on("rechat:new-message", (payload) => {
          if (payload?.sessionId !== resolvedSessionId) return;
          setMessages((prev) => [...prev, payload.message]);
        });
        socket.on("rechat:typing", (payload) => {
          if (payload?.sessionId !== resolvedSessionId) return;
          setTyping(!!payload.isTyping && payload.by === "agent");
        });

        socket.emit(
          "rechat:join",
          {
            sessionId: resolvedSessionId,
            visitorAnonId,
            token: user?.token || "",
          },
          () => {}
        );
      } catch (error) {
        setStatus("error");
      } finally {
        setLoading(false);
      }
    };

    setup();

    return () => {
      if (socketRef.current) socketRef.current.disconnect();
      socketRef.current = null;
      setTyping(false);
      setMessages([]);
      setSessionId("");
    };
  }, [isOpen, listingId, agentId, visitorAnonId, user?.token]);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages, typing]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || !sessionId) return;
    setInput("");

    const socket = socketRef.current;
    if (socket && socket.connected) {
      socket.emit("rechat:send", { text }, async (ack) => {
        if (!ack?.ok) {
          await axios.post(
            `${BASE_URL}/api/rechat/session/${sessionId}/message`,
            { text, visitorAnonId },
            { headers: { ...getAuthHeaders() } }
          );
        }
      });
    } else {
      const res = await axios.post(
        `${BASE_URL}/api/rechat/session/${sessionId}/message`,
        { text, visitorAnonId },
        { headers: { ...getAuthHeaders() } }
      );
      if (res?.data?.message) setMessages((prev) => [...prev, res.data.message]);
    }
  };

  const onInputChange = (value) => {
    setInput(value);
    const socket = socketRef.current;
    if (socket && socket.connected) {
      socket.emit("rechat:typing", { isTyping: true });
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        socket.emit("rechat:typing", { isTyping: false });
      }, 1200);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="rechat-overlay">
      <div className="rechat-modal">
        <div className="rechat-header">
          <div>
            <h5>Chat with Agent</h5>
            <small>{listingTitle || "Property"}</small>
          </div>
          <button type="button" onClick={onClose} className="rechat-close">
            x
          </button>
        </div>
        <div className="rechat-body">
          {loading ? <p>Loading chat...</p> : null}
          {!loading &&
            messages.map((m) => (
              <div
                key={m._id}
                className={`rechat-message ${m.senderType === "visitor" ? "mine" : "their"}`}
              >
                <div>{m.text}</div>
                <small>{new Date(m.createdAt).toLocaleString()}</small>
              </div>
            ))}
          {typing ? <div className="rechat-typing">Agent is typing...</div> : null}
          <div ref={bottomRef} />
        </div>
        <div className="rechat-footer">
          <input
            value={input}
            onChange={(e) => onInputChange(e.target.value)}
            placeholder="Type your message..."
            onKeyDown={(e) => {
              if (e.key === "Enter") sendMessage();
            }}
          />
          <button type="button" onClick={sendMessage}>
            Send
          </button>
        </div>
        <small className="rechat-status">Status: {status}</small>
      </div>
    </div>
  );
};

export default ListingChatModal;

